import { useEffect, useState } from "react";
import { icons as lucideIcons } from "lucide-react";

/* ─── Icon lookup for backend-supplied view icons ───
   A view's `icon` string is either a Lucide key ("tv-minimal") or a
   react-icons Font Awesome export name ("FaYoutube"). */

// Convert PascalCase component name to kebab-case key for storage
export const pascalToKebab = (str) =>
  str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

const kebabToPascal = (str) =>
  str
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");

// Build kebab-key → component map from full registry
const LUCIDE_ICON_MAP = {};
Object.entries(lucideIcons).forEach(([name, component]) => {
  LUCIDE_ICON_MAP[pascalToKebab(name)] = component;
});

const FALLBACK_ICON = lucideIcons.SlidersHorizontal;

// Font Awesome exports are PascalCase ("FaYoutube", "FaRegBell", "Fa500Px").
// The check is case-sensitive on purpose: Lucide keys are lowercase kebab, so
// "factory", "fan", "facebook" and "fast-forward" never match.
export const isFontAwesomeIcon = (key) =>
  typeof key === "string" && /^Fa[A-Z0-9]/.test(key);

/* ─── Font Awesome (lazy) ───
   The packs are ~1.5MB each, so they are only fetched once a view actually
   asks for an FA icon. FA6 is tried first, FA5 covers names that only exist
   there. The packs come in through ./iconPacks so they stay out of the main
   bundle (see the note in iconPacks/fa6.js). */
const faResolved = new Map(); // name → component | null (not in either pack)
const faPending = new Map(); // name → Promise

const packIcon = (pack, name) => (pack.default || pack)[name];

const loadFontAwesomeIcon = (name) => {
  if (!faPending.has(name)) {
    faPending.set(
      name,
      import("./iconPacks/fa6")
        .then(
          (fa6) =>
            packIcon(fa6, name) ||
            import("./iconPacks/fa").then((fa) => packIcon(fa, name)),
        )
        .then((component) => {
          faResolved.set(name, component || null);
          return component || null;
        })
        .catch((e) => {
          console.error(`[viewIcons] Failed to load "${name}":`, e);
          faPending.delete(name);
          return null;
        }),
    );
  }
  return faPending.get(name);
};

// One wrapper per name so callers get a stable component identity
const faComponents = new Map();

const fontAwesomeIcon = (name) => {
  if (!faComponents.has(name)) {
    const FaIcon = (props) => {
      const [Icon, setIcon] = useState(() => faResolved.get(name));

      useEffect(() => {
        if (Icon !== undefined) return;
        let alive = true;
        loadFontAwesomeIcon(name).then((component) => {
          if (alive) setIcon(() => component);
        });
        return () => {
          alive = false;
        };
      }, [Icon]);

      if (Icon === null) return <FALLBACK_ICON {...props} />;

      // FA glyphs are filled — a Lucide stroke width would outline them
      const { strokeWidth, ...rest } = props;

      // Hold the icon's box while the pack loads
      if (Icon === undefined) {
        const { size = "1em", className, style } = rest;
        return (
          <svg
            width={size}
            height={size}
            className={className}
            style={style}
            aria-hidden
          />
        );
      }

      return <Icon {...rest} />;
    };
    FaIcon.displayName = name;
    faComponents.set(name, FaIcon);
  }
  return faComponents.get(name);
};

const lucideIcon = (key) => {
  if (typeof key !== "string" || !key) return null;
  return LUCIDE_ICON_MAP[key] || lucideIcons[kebabToPascal(key)] || null;
};

// Returns a component, or null when a Lucide key is unknown
export const resolveViewIcon = (key) =>
  isFontAwesomeIcon(key) ? fontAwesomeIcon(key) : lucideIcon(key);

export const feedViewIcon = (key) => resolveViewIcon(key) || FALLBACK_ICON;
